import React, { useState, useRef } from 'react';

const API_BASE = `${process.env.REACT_APP_SERVER_BASE_URL || ''}/api`;

export default function PDFCompare({ onBack }) {
  const [pdf1, setPdf1]         = useState(null);
  const [pdf2, setPdf2]         = useState(null);
  const [pdf1Name, setPdf1Name] = useState('');
  const [pdf2Name, setPdf2Name] = useState('');
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const ref1 = useRef();
  const ref2 = useRef();

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFile = async (e, num) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file.');
      return;
    }
    const b64 = await toBase64(file);
    if (num === 1) { setPdf1(b64); setPdf1Name(file.name); }
    else           { setPdf2(b64); setPdf2Name(file.name); }
    setError('');
    setResult(null);
  };

  const handleCompare = async () => {
    if (!pdf1 || !pdf2) { setError('Please upload both PDF files first.'); return; }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res  = await fetch(`${API_BASE}/trucking/compare-pdf`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ pdf1, pdf2, pdf1Name, pdf2Name }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Comparison failed.');
      else         setResult(data);
    } catch {
      setError('Could not connect to server.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPdf1(null); setPdf2(null);
    setPdf1Name(''); setPdf2Name('');
    setResult(null); setError('');
    if (ref1.current) ref1.current.value = '';
    if (ref2.current) ref2.current.value = '';
  };

  return (
    <div className="app">
      <div className="bg-layer" />
      <header className="header">
        <div className="header-inner">
          <div className="logo-mark">RM</div>
          <div className="header-text">
            <h1 className="site-title">PDF Conditions Comparison</h1>
            <p className="site-subtitle">Compare Conditions of Sale between two PDFs</p>
          </div>
        </div>
      </header>

      <main className="main">
        <button className="back-btn" onClick={onBack}>← Back to Search</button>

        {/* Upload Section */}
        <section className="pdf-upload-section">
          <div className="pdf-upload-grid">

            {/* PDF 1 */}
            <div className={`pdf-upload-card ${pdf1 ? 'uploaded' : ''}`}>
              <div className="pdf-card-header">
                <span className="pdf-card-label">PDF 1</span>
                {pdf1 && <span className="pdf-check">✓ Uploaded</span>}
              </div>
              <div className="pdf-drop-area" onClick={() => ref1.current.click()}>
                {pdf1 ? (
                  <>
                    <div className="pdf-icon">📄</div>
                    <div className="pdf-filename">{pdf1Name}</div>
                    <div className="pdf-replace">Click to replace</div>
                  </>
                ) : (
                  <>
                    <div className="pdf-icon">📁</div>
                    <div className="pdf-upload-text">Click to upload PDF</div>
                    <div className="pdf-upload-hint">Conditions of Sale will be extracted</div>
                  </>
                )}
              </div>
              <input
                ref={ref1}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={e => handleFile(e, 1)}
              />
            </div>

            {/* VS divider */}
            <div className="pdf-vs">VS</div>

            {/* PDF 2 */}
            <div className={`pdf-upload-card ${pdf2 ? 'uploaded' : ''}`}>
              <div className="pdf-card-header">
                <span className="pdf-card-label">PDF 2</span>
                {pdf2 && <span className="pdf-check">✓ Uploaded</span>}
              </div>
              <div className="pdf-drop-area" onClick={() => ref2.current.click()}>
                {pdf2 ? (
                  <>
                    <div className="pdf-icon">📄</div>
                    <div className="pdf-filename">{pdf2Name}</div>
                    <div className="pdf-replace">Click to replace</div>
                  </>
                ) : (
                  <>
                    <div className="pdf-icon">📁</div>
                    <div className="pdf-upload-text">Click to upload PDF</div>
                    <div className="pdf-upload-hint">Conditions of Sale will be extracted</div>
                  </>
                )}
              </div>
              <input
                ref={ref2}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={e => handleFile(e, 2)}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="pdf-actions">
            <button className="save-btn" onClick={handleCompare} disabled={loading || !pdf1 || !pdf2}>
              {loading ? <><span className="spinner" /> Comparing…</> : '🔍 Compare Conditions of Sale'}
            </button>
            {(pdf1 || pdf2 || result) && (
              <button className="cancel-btn" onClick={handleReset}>↺ Reset</button>
            )}
          </div>
        </section>

        {/* Error */}
        {error && (
          <div className="message error-msg">
            <span className="msg-icon">⚠</span> {error}
          </div>
        )}

        {/* Result */}
        {result && (
          <section className="pdf-result-section">
            {/* Verdict */}
            <div className={`pdf-verdict ${result.match ? 'match' : 'different'}`}>
              <div className="verdict-icon">{result.match ? '✓' : '✗'}</div>
              <div className="verdict-text">
                <div className="verdict-title">
                  {result.match ? 'Conditions of Sale are IDENTICAL' : 'Conditions of Sale are DIFFERENT'}
                </div>
                <div className="verdict-subtitle">
                  {result.match
                    ? 'Both PDFs contain the same conditions of sale.'
                    : 'The conditions of sale differ between the two PDFs.'}
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="pdf-summary">
              <div className="pdf-summary-label">COMPARISON SUMMARY</div>
              <div className="pdf-summary-text">{result.summary}</div>
            </div>

            {/* Differences */}
            {!result.match && result.differences && result.differences.length > 0 && (
              <div className="pdf-differences">
                <div className="pdf-summary-label">KEY DIFFERENCES FOUND</div>
                {result.differences.map((diff, i) => (
                  <div className="pdf-diff-item" key={i}>
                    <div className="diff-number">{i + 1}</div>
                    <div className="diff-text">{diff}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Extracted text */}
            <div className="pdf-extracted-grid">
              <div className="pdf-extracted-card">
                <div className="pdf-extracted-label">📄 {pdf1Name} — Conditions of Sale</div>
                <div className="pdf-extracted-text">{result.conditions1}</div>
              </div>
              <div className="pdf-extracted-card">
                <div className="pdf-extracted-label">📄 {pdf2Name} — Conditions of Sale</div>
                <div className="pdf-extracted-text">{result.conditions2}</div>
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="footer">RM Trucking System · {new Date().getFullYear()}</footer>
    </div>
  );
}
